import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { map, startWith } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { AsyncPipe, NgIf } from '@angular/common';
import { Location } from '@shared/models';
import { EmployeeFormCardComponent } from '@ep/shared';
import { EmployeeFormComponent } from '@app/areas/merchants/forms/employee-form/employee-form.component';
import { EmployeeFormService } from '@app/areas/merchants/services/employee-form.service';
import { EmployeesApiService } from '@app/areas/merchants/services/employees.api.service';
import { LocationsApiService } from '@app/areas/merchants/services/locations.api.service';

@Component({
  selector: 'app-new-employee',
  templateUrl: './new-employee.component.html',
  styleUrls: ['./new-employee.component.scss'],
  standalone: true,
  imports: [EmployeeFormComponent, EmployeeFormCardComponent, AsyncPipe, NgIf],
})
export class NewEmployeeComponent implements OnInit, OnDestroy {
  private readonly employeeFormService = inject(EmployeeFormService);
  private readonly employeesApiService = inject(EmployeesApiService);
  private readonly locationsApiService = inject(LocationsApiService);
  private readonly messageService = inject(MessageService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected isSaveDisabled$ = this.employeeFormService.getForm().statusChanges.pipe(
    startWith(this.employeeFormService.getForm().status),
    map((status) => status !== 'VALID')
  );

  protected locations: Location[] = [];
  protected isSaving = false;
  private subs = new Subscription();

  ngOnInit(): void {
    this.employeeFormService.initializeForAdd();

    this.subs.add(
      this.route
        .parent!.parent!.paramMap.pipe(map((params) => Number(params.get('merchantId'))))
        .subscribe((merchantId) => this.loadLocations(merchantId))
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private loadLocations(merchantId: number): void {
    this.subs.add(
      this.locationsApiService.getByMerchantId(merchantId).subscribe((locations) => {
        this.locations = locations;
      })
    );
  }

  protected saveEmployee(): void {
    const form = this.employeeFormService.getForm();
    const locationId = this.locations[0]?.LocationId;
    if (!form.valid || !locationId) {
      return;
    }

    const merchantId = Number(this.route.parent?.parent?.snapshot.paramMap.get('merchantId'));
    this.isSaving = true;
    const formValue = form.getRawValue();

    this.subs.add(
      this.employeesApiService
        .create({
          MerchantId: merchantId,
          LocationId: locationId,
          NameFirst: formValue.Details.NameFirst!,
          NameLast: formValue.Details.NameLast!,
          EmailAddress: formValue.Details.EmailAddress!,
          PhoneNumber: formValue.Details.PhoneNumber?.replace(/\D/g, '') ?? '',
          AccessType: formValue.Access.AccessType.value,
          Pin: formValue.Terminal.Pins[0] || undefined,
        })
        .subscribe((employee) => {
          this.isSaving = false;
          if (employee) {
            this.router.navigate(['../', employee.EmployeeId], { relativeTo: this.route });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'ERROR: New Employee',
              detail: 'Something went wrong while creating the employee',
            });
          }
        })
    );
  }
}
